import React, { useEffect, useState } from 'react';
import { X, Copy, Check, Key, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import LoadingSpinner from '../common/LoadingSpinner';

function generateTempPassword() {
    return `TEMP_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Modale admin per resettare / impostare la password del gestore palestra.
 */
export default function ResetGymOwnerPasswordModal({ isOpen, onClose, gym, onResetPassword }) {
    const { t } = useTranslation();
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setPassword(generateTempPassword());
            setResult(null);
            setCopied(false);
            setLoading(false);
        }
    }, [isOpen, gym?.id]);

    if (!isOpen || !gym) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const trimmed = password.trim();
        if (trimmed.length < 6) {
            toast.error(t('admin.gymManager.resetPassword.minLength'));
            return;
        }

        setLoading(true);
        try {
            const data = await onResetPassword(gym.id, trimmed);
            if (data) {
                setResult(data);
                toast.success(t('admin.gymManager.resetPassword.success'));
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.error || t('admin.gymManager.resetPassword.error'));
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        const value = result?.temp_password;
        if (!value) return;
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success(t('admin.gymManager.resetPassword.copied'));
        } catch (err) {
            console.error(err);
        }
    };

    const handleClose = () => {
        setResult(null);
        setCopied(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="relative mx-4 w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900">
                <div className="flex items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-5 py-4 pr-12 dark:border-white/10 dark:bg-gray-800">
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900/40">
                            <Key className="h-5 w-5 text-orange-600 dark:text-orange-300" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t('admin.gymManager.resetPassword.title')}
                            </h3>
                            <p className="truncate text-sm text-gray-600 dark:text-white/70">
                                {gym.name}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="absolute right-3 top-3 rounded-lg p-2 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
                        aria-label={t('admin.gymManager.resetPassword.close')}
                    >
                        <X size={20} className="text-gray-500 dark:text-white/80" />
                    </button>
                </div>

                <div className="px-6 py-6">
                    {!result ? (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                {t('admin.gymManager.resetPassword.description')}
                            </p>
                            {gym.owner_username && (
                                <p className="text-sm text-gray-700 dark:text-gray-200">
                                    <span className="text-gray-500 dark:text-gray-400">
                                        {t('admin.gymManager.fieldUsername')}:{' '}
                                    </span>
                                    <span className="font-mono font-semibold">{gym.owner_username}</span>
                                </p>
                            )}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t('admin.gymManager.resetPassword.newPassword')}
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        minLength={6}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setPassword(generateTempPassword())}
                                        className="shrink-0 rounded-md bg-gray-100 px-3 py-2 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
                                        title={t('admin.gymManager.generatePassword')}
                                    >
                                        <RefreshCw size={18} />
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-orange-600 dark:text-orange-400">
                                    {t('admin.gymManager.resetPassword.tempHint')}
                                </p>
                            </div>
                            <div className="flex flex-wrap justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="rounded-xl bg-gray-100 px-5 py-2.5 font-medium text-gray-800 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
                                >
                                    {t('admin.gymManager.resetPassword.cancel')}
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex items-center gap-2 rounded-xl bg-orange-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-orange-700 disabled:opacity-50 dark:bg-indigo-600 dark:hover:brightness-110"
                                >
                                    {loading ? (
                                        <LoadingSpinner size="small" />
                                    ) : (
                                        <Key size={16} />
                                    )}
                                    {t('admin.gymManager.resetPassword.submit')}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/40">
                                    <Check className="h-6 w-6 text-green-600 dark:text-green-400" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-900 dark:text-white">
                                        {t('admin.gymManager.resetPassword.successTitle')}
                                    </h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-300">
                                        {result.note || t('admin.gymManager.resetPassword.successHint')}
                                    </p>
                                </div>
                            </div>

                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-950/60">
                                <div className="space-y-3">
                                    <div>
                                        <p className="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            {t('admin.gymManager.fieldUsername')}
                                        </p>
                                        <p className="break-all font-mono text-base text-gray-900 dark:text-white">
                                            {result.user?.username || gym.owner_username || '—'}
                                        </p>
                                    </div>
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                                        <div className="min-w-0 flex-1">
                                            <p className="mb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                {t('admin.gymManager.fieldPassword')}
                                            </p>
                                            <p className="break-all font-mono text-base text-gray-900 dark:text-white">
                                                {result.temp_password}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCopy}
                                            className="flex shrink-0 items-center gap-2 self-start rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700 dark:bg-indigo-600 dark:hover:brightness-110"
                                        >
                                            {copied ? <Check size={16} /> : <Copy size={16} />}
                                            {copied
                                                ? t('admin.gymManager.resetPassword.copied')
                                                : t('admin.gymManager.resetPassword.copy')}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="rounded-xl bg-gray-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-gray-700 dark:bg-indigo-600 dark:hover:brightness-110"
                                >
                                    {t('admin.gymManager.resetPassword.close')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
